import {
  CONTAINER_HEIGHT_IN_MMM,
  IRowPattern,
  IVesselParticularsSection,
  IVesselParticularsSectionLevel,
} from "@tedivo/tedivo-bay-grid-pure";
import { IVesselParticularsColors, IVesselParticularsUnits } from "./vesselParticularsTypes";

import { createSvgText } from "../helpers/createSvgText";
import { roundDec } from "@tedivo/tedivo-pure-helpers";

const SVG_NS = "http://www.w3.org/2000/svg";
const MONO_FONT_FAMILY = `"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace`;

const TITLE_HEIGHT = 12;
const HEADER_ROW_HEIGHT = 11;
const CONTAINER_WIDTH_MM = 2440;
/** Margin column on the left holding the tier numbers and bottom bases, like the Bay views */
const LEFT_GUTTER = 17;
const BOTTOM_PAD = 5;
/** Vertical position of the tier number inside its cell when a bottom base is shown under it (ratio from the cell top), matching createGridPart */
const NUMBER_SHIFTED_UP_Y_RATIO = 0.32;
/** Vertical position of the bottom base label inside its cell (ratio from the cell top), matching createGridPart */
const CG_LABEL_Y_RATIO = 0.74;
/** Height in px of a hatch-cover lid band */
const LID_HEIGHT = 4.5;

export interface IDrawnVesselParticularsSection {
  svgGroup: SVGGElement;
  width: number;
  height: number;
  /** Where the cells' coordinate space starts (below the title and header), in px from the group top */
  contentTop: number;
}

/** The drawn width of a section, without drawing it — used by the composer to group sections into rows */
export function calculateVesselParticularsSectionWidth(
  section: IVesselParticularsSection,
  pxPerMm: number,
): number {
  return LEFT_GUTTER + roundDec((section.xMaxMm - section.xMinMm) * pxPerMm, 2) + 2;
}

/**
 * Draws one plan cross-section: a bay title, a header strip with the row numbers
 * (plus their TCGs when the file has real ones), the container stacks of both
 * levels placed by TCG / bottom base, the stepped hull line and, in a left margin
 * column, the tier numbers with their bottom bases — styled like the Bay views.
 * All geometry is scaled with `pxPerMm`, shared by every section of the sheet.
 */
export function drawVesselParticularsBaySection({
  section,
  pxPerMm,
  colors,
  units,
  topVcgMm,
}: {
  section: IVesselParticularsSection;
  pxPerMm: number;
  colors: IVesselParticularsColors;
  units: IVesselParticularsUnits;
  /**
   * The VCG (mm) the content area starts at. The composer passes the same value
   * to every section of a sheet row so equal tiers coincide horizontally across
   * the sections. Default: this section's own top.
   */
  topVcgMm?: number;
}): IDrawnVesselParticularsSection {
  const { xMinMm, xMaxMm, minVcgMm, maxVcgMm } = section;
  const topVcg = topVcgMm !== undefined ? Math.max(topVcgMm, maxVcgMm) : maxVcgMm;

  const hasDisplayTcgs = Object.keys(section.rowTcgsMm).length > 0;
  const headerHeight = HEADER_ROW_HEIGHT * (hasDisplayTcgs ? 2 : 1);

  const contentWidth = roundDec((xMaxMm - xMinMm) * pxPerMm, 2);
  const contentTop = TITLE_HEIGHT + headerHeight + 2;
  const contentHeight = roundDec((topVcg - minVcgMm) * pxPerMm, 2);

  const xPx = (mm: number) => roundDec(LEFT_GUTTER + (mm - xMinMm) * pxPerMm, 2);
  const yPx = (vcgMm: number) => roundDec(contentTop + (topVcg - vcgMm) * pxPerMm, 2);

  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("data-bays", section.isoBays.join(","));

  // Title: "BAY 01" or "BAY 05/17"
  g.appendChild(
    createSvgText({
      text: `BAY ${section.isoBays.map((b) => String(Number(b)).padStart(2, "0")).join("/")}`,
      x: LEFT_GUTTER + contentWidth / 2,
      y: TITLE_HEIGHT - 5,
      fontSize: 8,
      textColor: colors.textColor,
      className: "vessel-particulars-bay-title",
    }),
  );

  drawRowHeader(g, section, xPx, contentWidth, headerHeight, hasDisplayTcgs, colors, units);

  const zeroLine = createLine(
    xPx(0),
    contentTop,
    xPx(0),
    contentTop + contentHeight,
    colors.zeroTcgStroke,
    0.5,
  );
  zeroLine.setAttribute("stroke-dasharray", "2,2");
  g.appendChild(zeroLine);

  if (section.above) drawContainers(g, section.above, xPx, yPx, pxPerMm, colors);
  if (section.below) drawContainers(g, section.below, xPx, yPx, pxPerMm, colors);

  if (section.above) {
    const hatchPath = buildSteppedPath(section.above, xPx, yPx);
    if (hatchPath) g.appendChild(createPath(hatchPath, colors.hatchStroke, 0.7));
  }

  // The hull is an open stepped line under the hold: vessels are concave there,
  // so no closed shape is drawn.
  if (section.below) {
    const steps = buildSteppedPath(section.below, xPx, yPx);
    if (steps) g.appendChild(createPath(steps, colors.hullStroke, 1.2));
  }

  drawLidBands(g, section, xPx, yPx, pxPerMm, colors);

  drawMaxHeights(g, section, xPx, yPx, colors);

  if (section.above) drawTierGutter(g, section.above, yPx, pxPerMm, colors, units);
  if (section.below) drawTierGutter(g, section.below, yPx, pxPerMm, colors, units);

  return {
    svgGroup: g,
    width: LEFT_GUTTER + contentWidth + 2,
    height: contentTop + contentHeight + BOTTOM_PAD,
    contentTop,
  };
}

/** Header strip: the row numbers and, when the file has real TCGs, their values below */
function drawRowHeader(
  g: SVGGElement,
  section: IVesselParticularsSection,
  xPx: (mm: number) => number,
  contentWidth: number,
  headerHeight: number,
  hasDisplayTcgs: boolean,
  colors: IVesselParticularsColors,
  units: IVesselParticularsUnits,
) {
  const top = TITLE_HEIGHT;

  const frame = document.createElementNS(SVG_NS, "rect");
  frame.setAttribute("x", `${LEFT_GUTTER}`);
  frame.setAttribute("y", `${top}`);
  frame.setAttribute("width", `${contentWidth}`);
  frame.setAttribute("height", `${headerHeight}`);
  frame.setAttribute("fill", "none");
  frame.setAttribute("stroke", colors.tableStroke);
  frame.setAttribute("stroke-width", "0.6");
  g.appendChild(frame);

  if (hasDisplayTcgs) {
    g.appendChild(
      createLine(
        LEFT_GUTTER,
        top + HEADER_ROW_HEIGHT,
        LEFT_GUTTER + contentWidth,
        top + HEADER_ROW_HEIGHT,
        colors.tableStroke,
        0.4,
      ),
    );
  }

  // Shrink the TCG font when the columns are narrow (e.g. around a centerline row)
  const tcgTexts = section.rows.map((row) => {
    const tcg = section.rowTcgsMm[row];
    return tcg !== undefined
      ? units.lengthUnits.fromValueToFormattedDisplay(units.tcgTransposer.fromValueToDisplay(tcg))
      : "";
  });
  const rowXs = section.rows
    .map((row) => section.rowXsMm[row])
    .filter((x): x is number => x !== undefined)
    .map((x) => xPx(x));
  let minColumnWidth = contentWidth;
  for (let i = 1; i < rowXs.length; i += 1) {
    const gap = Math.abs(rowXs[i] - rowXs[i - 1]);
    if (gap < minColumnWidth) minColumnWidth = gap;
  }
  const longestTcgText = Math.max(1, ...tcgTexts.map((t) => t.length));
  const tcgFontSize = Math.max(2.4, Math.min(4.5, minColumnWidth / (0.62 * longestTcgText)));

  section.rows.forEach((row, idx) => {
    const x = section.rowXsMm[row];
    if (x === undefined) return;

    // Column separator at the midpoint with the next row
    if (idx < section.rows.length - 1) {
      const nextX = section.rowXsMm[section.rows[idx + 1]];
      if (nextX !== undefined) {
        const midX = xPx((x + nextX) / 2);
        g.appendChild(createLine(midX, top, midX, top + headerHeight, colors.tableStroke, 0.4));
      }
    }

    g.appendChild(
      createSvgText({
        text: row,
        x: xPx(x),
        y: top + HEADER_ROW_HEIGHT - 3,
        fontSize: 6,
        textColor: colors.gridNumberColor,
      }),
    );

    if (tcgTexts[idx]) {
      g.appendChild(
        createSvgText({
          text: tcgTexts[idx],
          x: xPx(x),
          y: top + headerHeight - 3,
          fontSize: tcgFontSize,
          textColor: Number(row) % 2 === 0 ? colors.tcgPortColor : colors.tcgStbdColor,
          fontFamily: MONO_FONT_FAMILY,
          letterSpacing: "-0.05em",
        }),
      );
    }
  });
}

/**
 * One rect per stacked tier. The cells are placed on the level's shared tier
 * bands (tierBottomBases), not on each row's own cumulative stack, so equal
 * tiers coincide horizontally across all the rows of the section. The gutter's
 * tier numbers use the same bands, so cells and labels always line up.
 */
function drawContainers(
  g: SVGGElement,
  level: IVesselParticularsSectionLevel,
  xPx: (mm: number) => number,
  yPx: (vcgMm: number) => number,
  pxPerMm: number,
  colors: IVesselParticularsColors,
) {
  const halfWidthMm = CONTAINER_WIDTH_MM / 2;

  const bands = level.tierBottomBases;
  const bandIndexByTier = new Map<number, number>(bands.map((b, idx) => [Number(b.tier), idx]));

  const bandOf = (isoTier: number): { baseMm: number; heightMm: number } | undefined => {
    const idx = bandIndexByTier.get(isoTier);
    if (idx === undefined) return undefined;
    const baseMm = bands[idx].bottomBaseMm;
    const next = bands[idx + 1]?.bottomBaseMm;
    const prev = bands[idx - 1]?.bottomBaseMm;
    const heightMm =
      next !== undefined && next > baseMm
        ? next - baseMm
        : prev !== undefined && baseMm > prev
          ? baseMm - prev
          : CONTAINER_HEIGHT_IN_MMM;
    return { baseMm, heightMm };
  };

  level.layout.rows.forEach((row) => {
    const { tcg, tierBasesMm } = row;

    tierBasesMm.forEach((rowBaseMm, idx) => {
      const isoTier = Number(row.bottomTier) + idx * 2;
      const band = bandOf(isoTier) ?? {
        baseMm: rowBaseMm,
        heightMm: tierHeightOf(tierBasesMm, idx),
      };

      const rect = document.createElementNS(SVG_NS, "rect");
      rect.setAttribute("x", `${xPx(tcg - halfWidthMm)}`);
      rect.setAttribute("y", `${yPx(band.baseMm + band.heightMm)}`);
      rect.setAttribute("width", `${roundDec(CONTAINER_WIDTH_MM * pxPerMm, 2)}`);
      rect.setAttribute("height", `${roundDec(band.heightMm * pxPerMm, 2)}`);
      rect.setAttribute("fill", colors.containerFill);
      rect.setAttribute("stroke", colors.containerStroke);
      rect.setAttribute("stroke-width", "0.4");
      rect.setAttribute("vector-effect", "non-scaling-stroke");
      g.appendChild(rect);
    });
  });
}

/**
 * Tier numbers (and their bottom bases, when real) in the left margin column,
 * vertically centered on their cells — the same arrangement and roles as the
 * Bay views' grid numbers.
 */
function drawTierGutter(
  g: SVGGElement,
  level: IVesselParticularsSectionLevel,
  yPx: (vcgMm: number) => number,
  pxPerMm: number,
  colors: IVesselParticularsColors,
  units: IVesselParticularsUnits,
) {
  const showBases = level.bottomBasesAreReal;
  const x = LEFT_GUTTER * 0.5;

  const bases = level.tierBottomBases;

  bases.forEach(({ tier, bottomBaseMm }, idx) => {
    const nextBase = bases[idx + 1]?.bottomBaseMm;
    const heightMm =
      nextBase !== undefined && nextBase > bottomBaseMm
        ? nextBase - bottomBaseMm
        : CONTAINER_HEIGHT_IN_MMM;

    const cellTopVcg = bottomBaseMm + heightMm;

    const tierNumberY = yPx(cellTopVcg - heightMm * (showBases ? NUMBER_SHIFTED_UP_Y_RATIO : 0.5));
    g.appendChild(
      createSvgText({
        text: tier,
        x,
        y: tierNumberY,
        fontSize: Math.min(5, roundDec(heightMm * pxPerMm * 0.34, 2)),
        textColor: colors.gridNumberColor,
      }),
    );

    if (showBases) {
      g.appendChild(
        createSvgText({
          text: units.lengthUnits.fromValueToFormattedDisplay(
            units.vcgTransposer.fromValueToDisplay(bottomBaseMm),
          ),
          x,
          y: yPx(cellTopVcg - heightMm * CG_LABEL_Y_RATIO),
          fontSize: 3.6,
          textColor: colors.bottomBaseColor,
          fontFamily: MONO_FONT_FAMILY,
          letterSpacing: "-0.05em",
        }),
      );
    }
  });
}

/**
 * The hatch-cover lids as bands across their port..starboard row columns, drawn
 * entirely under the deck cells that rest on them — following the Bay views'
 * drawLids, including the overlapPort / overlapStarboard notches between
 * neighbouring lids.
 */
function drawLidBands(
  g: SVGGElement,
  section: IVesselParticularsSection,
  xPx: (mm: number) => number,
  yPx: (vcgMm: number) => number,
  pxPerMm: number,
  colors: IVesselParticularsColors,
) {
  if (section.lids.length === 0) return;

  const halfWidthMm = CONTAINER_WIDTH_MM / 2;
  const OVERLAP_WIDTH = roundDec(CONTAINER_WIDTH_MM * pxPerMm * 0.15, 3);
  const OVERLAP_HEIGHT = roundDec(LID_HEIGHT * 0.5, 3);

  const rowIndex = (row: string) => section.rows.indexOf(row as IRowPattern);

  // Sorted Port to Starboard, so each lid knows its neighbours for the notches
  const lidsSorted = section.lids
    .slice()
    .sort((a, b) => rowIndex(a.portIsoRow) - rowIndex(b.portIsoRow));

  lidsSorted.forEach((lid, idx) => {
    const prevLid = idx > 0 ? lidsSorted[idx - 1] : undefined;
    const nextLid = idx < lidsSorted.length - 1 ? lidsSorted[idx + 1] : undefined;

    let portIdx = rowIndex(lid.portIsoRow);
    let stbdIdx = rowIndex(lid.starboardIsoRow);
    if (portIdx < 0 && stbdIdx < 0) return;
    if (portIdx < 0) portIdx = 0;
    if (stbdIdx < 0) stbdIdx = section.rows.length - 1;

    const portX = section.rowXsMm[section.rows[portIdx]];
    const stbdX = section.rowXsMm[section.rows[stbdIdx]];
    if (portX === undefined || stbdX === undefined) return;

    const hatchVcg = hatchVcgOf(section, portIdx, stbdIdx);
    if (hatchVcg === undefined) return;

    const left = xPx(portX - halfWidthMm);
    const right = xPx(stbdX + halfWidthMm);
    // Entirely under the cells that rest on it: the band's top edge is the deck
    // bottom base. Without an above level, it sits on top of the hold instead.
    const yOffset = roundDec(section.above ? yPx(hatchVcg) : yPx(hatchVcg) - LID_HEIGHT, 2);

    // Same outline logic as the Bay views' drawLids
    const leftTop = lid.overlapPort
      ? `M${left},${yOffset + OVERLAP_HEIGHT} h-${OVERLAP_WIDTH} v-${OVERLAP_HEIGHT}`
      : prevLid?.overlapStarboard
        ? `M${left + OVERLAP_WIDTH},${yOffset}`
        : `M${left},${yOffset}`;

    const rightTop = lid.overlapStarboard
      ? `L${right + OVERLAP_WIDTH},${yOffset} v${OVERLAP_HEIGHT} h-${OVERLAP_WIDTH}`
      : nextLid?.overlapPort
        ? `L${right - OVERLAP_WIDTH},${yOffset} V${yOffset + LID_HEIGHT - OVERLAP_HEIGHT} h${OVERLAP_WIDTH}`
        : `L${right},${yOffset}`;

    const rightBottom = `L${right},${yOffset + LID_HEIGHT}`;

    const leftBottom = prevLid?.overlapStarboard
      ? `L${left},${yOffset + LID_HEIGHT} v-${OVERLAP_HEIGHT} h${OVERLAP_WIDTH}`
      : `L${left},${yOffset + LID_HEIGHT}`;

    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", [leftTop, rightTop, rightBottom, leftBottom, "z"].join(" "));
    path.setAttribute("fill", colors.lidBackgroundColor);
    path.setAttribute("stroke", colors.lidBorderColor);
    path.setAttribute("stroke-width", "0.7");
    path.setAttribute("vector-effect", "non-scaling-stroke");
    path.setAttribute("class", "vessel-particulars-lid");
    g.appendChild(path);

    if (lid.label) {
      g.appendChild(
        createSvgText({
          text: lid.label,
          x: roundDec((left + right) / 2, 2),
          y: yOffset + LID_HEIGHT - 1.2,
          fontSize: 3.4,
          textColor: colors.lidTextColor,
        }),
      );
    }
  });
}

/** VCG of the hatch line over rows [portIdx..stbdIdx]: the most common above-deck bottom base, or the hold's stack top */
function hatchVcgOf(
  section: IVesselParticularsSection,
  portIdx: number,
  stbdIdx: number,
): number | undefined {
  const coveredRows = new Set(section.rows.slice(portIdx, stbdIdx + 1));

  if (section.above) {
    const bases = section.above.layout.rows
      .filter((r) => coveredRows.has(r.row))
      .map((r) => r.bottomBaseMm);
    if (bases.length) return modeOfValues(bases);
    return section.above.layout.minBB;
  }

  if (section.below) {
    const tops = section.below.layout.rows
      .filter((r) => coveredRows.has(r.row))
      .map((r) => {
        const last = r.tierBasesMm.length - 1;
        return r.tierBasesMm[last] + tierHeightOf(r.tierBasesMm, last);
      });
    if (tops.length) return Math.max(...tops);
  }

  return undefined;
}

function modeOfValues(values: number[]): number {
  const counts = new Map<number, number>();
  let best = values[0];
  let bestCount = 0;
  values.forEach((v) => {
    const c = (counts.get(v) ?? 0) + 1;
    counts.set(v, c);
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  });
  return best;
}

/** Stepped line following the bottom bases of a level, Port to Starboard */
function buildSteppedPath(
  level: IVesselParticularsSectionLevel,
  xPx: (mm: number) => number,
  yPx: (vcgMm: number) => number,
): string | undefined {
  const rows = level.layout.rows;
  if (rows.length === 0) return undefined;

  const parts: string[] = [`M${xPx(rows[0].prevTcgMid)},${yPx(rows[0].bottomBaseMm)}`];
  rows.forEach((row) => {
    const y = yPx(row.bottomBaseMm);
    parts.push(`L${xPx(row.prevTcgMid)},${y}`);
    parts.push(`L${xPx(row.nextTcgMid)},${y}`);
  });

  return parts.join(" ");
}

function drawMaxHeights(
  g: SVGGElement,
  section: IVesselParticularsSection,
  xPx: (mm: number) => number,
  yPx: (vcgMm: number) => number,
  colors: IVesselParticularsColors,
) {
  [section.above, section.below].forEach((level) => {
    level?.layout.rows.forEach((row) => {
      if (row.maxHeightMm === undefined) return;
      const y = yPx(row.bottomBaseMm + row.maxHeightMm);
      const line = createLine(
        xPx(row.prevTcgMid),
        y,
        xPx(row.nextTcgMid),
        y,
        colors.maxHeightStroke,
        0.7,
      );
      line.setAttribute("stroke-dasharray", "3,1.5");
      g.appendChild(line);
    });
  });
}

function tierHeightOf(tierBasesMm: number[], idx: number): number {
  const nextBase = tierBasesMm[idx + 1];
  const prevBase = tierBasesMm[idx - 1];
  return nextBase !== undefined
    ? nextBase - tierBasesMm[idx]
    : prevBase !== undefined
      ? tierBasesMm[idx] - prevBase
      : CONTAINER_HEIGHT_IN_MMM;
}

function createLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  stroke: string,
  width: number,
): SVGLineElement {
  const line = document.createElementNS(SVG_NS, "line");
  line.setAttribute("x1", `${x1}`);
  line.setAttribute("y1", `${y1}`);
  line.setAttribute("x2", `${x2}`);
  line.setAttribute("y2", `${y2}`);
  line.setAttribute("stroke", stroke);
  line.setAttribute("stroke-width", `${width}`);
  line.setAttribute("vector-effect", "non-scaling-stroke");
  return line;
}

function createPath(d: string, stroke: string, width: number): SVGPathElement {
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", d);
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", stroke);
  path.setAttribute("stroke-width", `${width}`);
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  path.setAttribute("vector-effect", "non-scaling-stroke");
  return path;
}
